import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight } from 'lucide-react';
import { getEnabledShiftReportSections, type ShiftReportTemplate, type ShiftReportValues } from '@/utils/shiftReportTemplate';
import { StaffSelectField } from '@/components/staff/StaffSelectField';
import { useShiftConfig } from '@/hooks/useShiftConfig';
import { DEFAULT_SHIFT_FRAMES } from '@/utils/shiftConfig';

export type ShiftDetailsData = ShiftReportValues;

export interface ShiftTotalRow {
  key: string;
  label: string;
  count: number;
  names: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details: ShiftDetailsData;
  template: ShiftReportTemplate;
  onSave: (details: ShiftDetailsData) => void;
  lockedFields?: Set<string>;
  shiftTotals?: ShiftTotalRow[];
}

export const ShiftDetailsDialog: React.FC<Props> = ({ open, onOpenChange, details, template, onSave, lockedFields, shiftTotals }) => {
  const enabledSections = getEnabledShiftReportSections(template);
  const { data: configuredShifts = DEFAULT_SHIFT_FRAMES } = useShiftConfig();
  const shiftOptions = configuredShifts.length ? configuredShifts : DEFAULT_SHIFT_FRAMES;
  const [local, setLocal] = useState<ShiftDetailsData>(details);

  useEffect(() => {
    if (open) setLocal(details);
  }, [open, details]);

  const update = (k: keyof ShiftDetailsData, v: string) =>
    setLocal((prev) => ({ ...prev, [k]: v }));

  const renderMealSection = (section: any) => {
    const mealTypes: Array<{ id: string; label: string; menuKey: string; menuLabel: string }> = [
      { id: 'breakfast', label: 'Breakfast', menuKey: 'breakfastMenu', menuLabel: 'Menu' },
      { id: 'lunch', label: 'Lunch', menuKey: 'lunchMenu', menuLabel: 'Menu' },
      { id: 'dinner', label: 'Dinner', menuKey: 'dinnerMenu', menuLabel: 'Menu' },
      { id: 'milk', label: 'Milk', menuKey: 'milkBrand', menuLabel: 'Brand' },
      { id: 'juice', label: 'Juice', menuKey: 'juiceBrand', menuLabel: 'Brand' },
    ];

    // Detect which meals are enabled (any of their fields present in the template section)
    const enabledIds = new Set(section.fields.map((f: any) => f.id.toLowerCase()));
    const isMealEnabled = (prefix: string) =>
      mealTypes.some((m) => m.id === prefix) &&
      Array.from(enabledIds).some((id: any) => typeof id === 'string' && id.startsWith(prefix));

    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {section.title}
        </h3>
        <div className="hidden md:grid grid-cols-[110px_70px_70px_70px_1fr_120px] gap-2 px-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Item</span>
          <span>Received</span>
          <span>Distrib.</span>
          <span>Discarded</span>
          <span>Menu / Brand</span>
          <span>Shift</span>
        </div>
        <div className="border rounded-md divide-y">
          {mealTypes.filter((m) => isMealEnabled(m.id)).map((meal) => {
            const prefix = meal.id;
            const recId = `${prefix}Received`;
            const distId = `${prefix}Distributed`;
            const discId = `${prefix}Discarded`;
            const shiftId = `${prefix}Shift`;
            const received = local[recId] || '';
            const distributed = local[distId] || '';
            const discarded = local[discId] || '';
            const menu = local[meal.menuKey] || '';
            const shiftVal = local[shiftId] || '';
            return (
              <div
                key={meal.id}
                className="grid grid-cols-3 md:grid-cols-[110px_70px_70px_70px_1fr_120px] gap-2 px-2 py-3 items-end"
              >
                <div className="font-medium text-sm col-span-3 md:col-span-1 md:self-center">{meal.label}</div>
                <div className="space-y-1">
                  <span className="md:hidden block text-[10px] uppercase tracking-wide text-muted-foreground">Received</span>
                  <Input
                    type="number"
                    min="0"
                    value={received}
                    onChange={(e) => update(recId, e.target.value)}
                    readOnly={lockedFields?.has(recId)}
                    className={cn('h-8 text-xs', lockedFields?.has(recId) && 'bg-background font-medium')}
                  />
                </div>
                <div className="space-y-1">
                  <span className="md:hidden block text-[10px] uppercase tracking-wide text-muted-foreground">Distributed</span>
                  <Input
                    type="number"
                    min="0"
                    value={distributed}
                    onChange={(e) => update(distId, e.target.value)}
                    readOnly={lockedFields?.has(distId)}
                    className={cn('h-8 text-xs', lockedFields?.has(distId) && 'bg-background font-medium')}
                  />
                </div>
                <div className="space-y-1">
                  <span className="md:hidden block text-[10px] uppercase tracking-wide text-muted-foreground">Discarded</span>
                  <Input
                    type="number"
                    min="0"
                    value={discarded}
                    onChange={(e) => update(discId, e.target.value)}
                    readOnly={lockedFields?.has(discId)}
                    className={cn('h-8 text-xs', lockedFields?.has(discId) && 'bg-background font-medium')}
                  />
                </div>
                <div className="space-y-1 col-span-3 md:col-span-1">
                  <span className="md:hidden block text-[10px] uppercase tracking-wide text-muted-foreground">{meal.menuLabel}</span>
                  <Input
                    value={menu}
                    onChange={(e) => update(meal.menuKey, e.target.value)}
                    readOnly={lockedFields?.has(meal.menuKey)}
                    placeholder={meal.menuLabel}
                    className={cn('h-8 text-xs', lockedFields?.has(meal.menuKey) && 'bg-background font-medium')}
                  />
                </div>
                <div className="space-y-1 col-span-3 md:col-span-1">
                  <span className="md:hidden block text-[10px] uppercase tracking-wide text-muted-foreground">Shift</span>
                  <Select value={shiftVal || undefined} onValueChange={(v) => update(shiftId, v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Shift" />
                    </SelectTrigger>
                    <SelectContent>
                      {shiftOptions.map((s) => (
                        <SelectItem key={s.id} value={s.id} className="text-xs">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {Number(discarded) > 0 && (
                  <Badge variant="outline" className="md:hidden col-span-3 w-fit text-[10px] bg-[hsl(var(--discard))] text-slate-50 border-transparent">
                    {discarded} discarded
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden grid grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader>
          <DialogTitle>Shift Details</DialogTitle>
          <p className="text-sm text-muted-foreground">
            These fields appear on the Print and PDF export of this shift. Saved per date and shift.
          </p>
        </DialogHeader>
        <ScrollArea className="max-h-[68vh] pr-4">
          <div className="space-y-6 py-2">
            {shiftTotals && shiftTotals.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Shift Totals
                </h3>
                <div className="border rounded-md divide-y">
                  {shiftTotals.map((row) => (
                    <Collapsible key={row.key}>
                      <CollapsibleTrigger
                        disabled={row.count === 0}
                        className={cn(
                          'group w-full flex items-center justify-between px-3 py-2 text-sm',
                          row.count === 0 ? 'opacity-50' : 'hover:bg-muted/50'
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <ChevronRight className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90" />
                          <span className="font-medium">{row.label}</span>
                        </span>
                        <Badge variant="outline" className="text-xs font-semibold">{row.count}</Badge>
                      </CollapsibleTrigger>
                      {row.names.length > 0 && (
                        <CollapsibleContent>
                          <div className="px-9 pb-3 pt-1 text-xs text-muted-foreground space-y-0.5">
                            {row.names.map((name, i) => (
                              <div key={`${row.key}-${i}`}>{name}</div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      )}
                    </Collapsible>
                  ))}
                </div>
              </div>
            )}
            {enabledSections.map((section) => (
              section.id === 'meals' ? renderMealSection(section) : (
                <div key={section.id} className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {section.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {section.fields.map((field) => (
                      <div key={field.id} className="space-y-1.5">
                        <Label>{field.label}</Label>
                        {field.inputType === 'staff' || field.inputType === 'staffMulti' ? (
                          <StaffSelectField
                            value={local[field.id] || ''}
                            onChange={(v) => update(field.id, v)}
                            multi={field.inputType === 'staffMulti'}
                            placeholder={field.placeholder}
                            readOnly={lockedFields?.has(field.id)}
                          />
                        ) : (
                          <Input
                            value={local[field.id] || ''}
                            onChange={(e) => update(field.id, e.target.value)}
                            placeholder={field.placeholder}
                            readOnly={lockedFields?.has(field.id)}
                            className={cn(
                              'focus-visible:ring-1 focus-visible:ring-muted-foreground/30 focus-visible:ring-offset-0 focus-visible:border-muted-foreground/40',
                              lockedFields?.has(field.id) && 'bg-muted font-medium'
                            )}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </ScrollArea>
        <div className="flex gap-2 justify-end pt-3 border-t bg-background">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(local)}>Save Details</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};